import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { GoogleMap, Marker, InfoWindow, OverlayView, Polyline } from '@react-google-maps/api'
import { mapBus } from '../../assets'
import { secureApi } from '../../Config/api'
import { GET } from '../../utils/apis'
import { tableDateFormate, translations } from '../../utils/constants'
import { useGoogleMaps } from '../../utils/googleMapsLoader'
import TraceEndpoints from '../TraceEndpoints/TraceEndpoints'
import CustomModal from '../CustomModal/CustomModal'
import VehicleRoute from '../VehicleRoute/VehicleRoute'
import moment from 'moment'

const EMPTY_STOPS = { past: [], current: null, future: [] }

// Stop-name labels only at street-level zoom, same as the main map.
const STOP_LABEL_MIN_ZOOM = 16

// One colour per direction of the vehicle's assigned line (outbound /
// return) - distinct from the blue matched trip and the red GPS trace.
// Exported so the GTFS line drawer can colour its direction chips the same.
export const DIRECTION_COLORS = ['#2e7d32', '#7b1fa2', '#ef6c00', '#00838f']

const containerStyle = {
  width: '100%',
  height: '400px',
}

const offsetPolylinePath = (path, offsetMeters = 15) => {
  if (!window.google?.maps?.geometry || !Array.isArray(path) || path.length < 2) return path;

  const offsetPath = [];

  for (let i = 0; i < path.length - 1; i++) {
    const start = new window.google.maps.LatLng(path[i].lat, path[i].lng);
    const end = new window.google.maps.LatLng(path[i + 1].lat, path[i + 1].lng);

    const heading = window.google.maps.geometry.spherical.computeHeading(start, end);
    const offsetHeading = heading + 90;

    const offsetStart = window.google.maps.geometry.spherical.computeOffset(start, offsetMeters, offsetHeading);
    const offsetEnd = window.google.maps.geometry.spherical.computeOffset(end, offsetMeters, offsetHeading);

    if (i === 0) {
      offsetPath.push({ lat: offsetStart.lat(), lng: offsetStart.lng() });
    }

    offsetPath.push({ lat: offsetEnd.lat(), lng: offsetEnd.lng() });
  }

  return offsetPath;
}


// lineDirections (optional): the assigned line's directions as returned by
// get-gtfs-line-detail ({ direction_id, headsign, shape, stops }). Each is
// drawn as its own coloured polyline so the vehicle can be read against its
// whole line even when no live trip is matched (or there is no GPS at all).
// A direction may carry its own `color`; otherwise one is picked by index.
// showLineEndpoints: mark each drawn direction's capolinea (first / last
// stop) with the shared S / E markers - used where no bus is on the map.
const VehicleMap = ({ deviceInfo, vehicleDetails, language, selectedDate, isLoading, lineDirections, showLineEndpoints }) => {
  const { isLoaded: isMapScriptLoaded } = useGoogleMaps()
  const [map, setMap] = useState(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [zoom, setZoom] = useState(14)
  const [center, setCenter] = useState(null)
  const [polylinePath, setPolylinePath] = useState([])
  const [infoOpen, setInfoOpen] = useState(false)
  // GTFS schedule for this vehicle (official line geometry + stops), same as
  // the main map: when present it replaces the raw GPS trace.
  const [scheduleRoute, setScheduleRoute] = useState(null)
  // First schedule response for the current day-selection has landed — the
  // route panel keeps its skeleton until both this and the stops call are in
  // (same rule as the main map, which otherwise flashed "no route data").
  const [scheduleLoaded, setScheduleLoaded] = useState(false)
  const [selectedStopInfo, setSelectedStopInfo] = useState(null)
  // Vehicle Route side panel — the same panel the main map opens on the
  // second click of a bus: 1st click = identity tooltip, 2nd = panel.
  const [routeModalOpen, setRouteModalOpen] = useState(false)
  const [vehicleStops, setVehicleStops] = useState(EMPTY_STOPS)
  const [isStopsLoading, setIsStopsLoading] = useState(false)

  const isFirstLoad = useRef(true)
  // Which (vehicle, line) the viewport was last fitted to - fit once, then
  // leave the user's pan/zoom alone across polls.
  const fittedKey = useRef(null)
  const vehicleId = vehicleDetails?.id

  const hasVehiclePosition =
    Number.isFinite(parseFloat(deviceInfo?.lat)) && Number.isFinite(parseFloat(deviceInfo?.lng))

  // Drawable geometry per direction: the GTFS shape when the feed ships one,
  // else the stop sequence joined by straight segments.
  const directionShapes = useMemo(
    () =>
      (lineDirections || [])
        .map((d, i) => {
          const fromShape = (d?.shape || [])
            .map((p) => ({ lat: parseFloat(p?.lat), lng: parseFloat(p?.lng) }))
            .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
          const stops = (d?.stops || [])
            .map((s) => ({ ...s, lat: parseFloat(s?.stop_lat), lng: parseFloat(s?.stop_lng) }))
            .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng))
          const path = fromShape.length > 1 ? fromShape : stops.map((s) => ({ lat: s.lat, lng: s.lng }))
          return {
            key: d?.direction_id ?? i,
            headsign: d?.headsign,
            color: d?.color || DIRECTION_COLORS[i % DIRECTION_COLORS.length],
            path,
            stops,
          }
        })
        .filter((d) => d.path.length > 1),
    [lineDirections]
  )

  // The router platform's last_time arrives as 'YYYY-MM-DD HH:mm:ss' (or a
  // unix epoch on some firmware); render just the clock, like the platform's
  // own "Report time", so positions can be compared across dashboards.
  const formatReportTime = (t) => {
    if (!t) return null
    const m = /^\d{10}$/.test(String(t))
      ? moment.unix(Number(t))
      : moment(t, ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true)
    return m.isValid() ? m.format('HH:mm:ss') : String(t)
  }

  useEffect(() => {
    const isToday = moment(selectedDate).isSame(new Date(), 'day')
    if (!vehicleId || !isToday) {
      setScheduleRoute(null)
      // Nothing to wait for on past days / unattached vehicles.
      setScheduleLoaded(true)
      return
    }

    let cancelled = false
    let missStreak = 0
    setScheduleLoaded(false)
    const fetchSchedule = async (silent) => {
      try {
        const sr = await secureApi.get(`${GET?.GET_VEHICLE_SCHEDULE_ROUTE}?vehicle_id=${vehicleId}`)
        if (cancelled) return
        setScheduleRoute((prev) => {
          const usable = !!(sr?.success && (sr?.schedule_available || !prev?.schedule_available))
          // "Run finished" / "waiting at the start" are verdicts, not losses:
          // the route comes off the map at once.
          const definitive =
            !!sr?.success && !sr?.schedule_available && ['trip_completed', 'awaiting_departure'].includes(sr?.reason)
          // A silent poll that briefly loses the schedule keeps the current
          // one; only a sustained loss (3 polls) replaces it.
          if (silent && !usable && !definitive && prev) {
            missStreak += 1
            if (missStreak < 3) return prev
          }
          missStreak = 0
          return sr?.success ? sr : null
        })
      } catch (err) {
        if (!cancelled && !silent) setScheduleRoute(null)
      } finally {
        if (!cancelled) setScheduleLoaded(true)
      }
    }

    fetchSchedule(false)
    // Same cadence as the main map's panel poll — delays/ETAs move.
    const id = setInterval(() => fetchSchedule(true), 15000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [vehicleId, selectedDate])

  // Stops (past / current / future) for the panel — identical call to the
  // main map's. silent: refresh without the skeleton (live refresh).
  const fetchVehicleStops = useCallback(
    async ({ silent = false } = {}) => {
      if (!vehicleId) return
      if (!silent) setIsStopsLoading(true)
      try {
        const formattedDate = moment(selectedDate).isValid()
          ? moment(selectedDate).format(tableDateFormate)
          : null
        const res = await secureApi.get(
          `${GET?.GET_VEHICLE_STOPS}?vehicle_id=${vehicleId}&date=${formattedDate}`
        )
        if (res?.success) {
          setVehicleStops({ past: res?.past || [], current: res?.current || null, future: res?.future || [] })
        } else if (!silent) {
          setVehicleStops(EMPTY_STOPS)
        }
      } catch (err) {
        if (!silent) setVehicleStops(EMPTY_STOPS)
      } finally {
        if (!silent) setIsStopsLoading(false)
      }
    },
    [vehicleId, selectedDate]
  )

  // While the panel is open on the live day, keep its stops/ETAs moving on
  // the same cadence as the schedule poll.
  useEffect(() => {
    if (!routeModalOpen || !vehicleId) return
    if (!moment(selectedDate).isSame(new Date(), 'day')) return
    const id = setInterval(() => fetchVehicleStops({ silent: true }), 15000)
    return () => clearInterval(id)
  }, [routeModalOpen, vehicleId, selectedDate, fetchVehicleStops])

  // A new day-selection invalidates whatever the panel was showing.
  useEffect(() => {
    setRouteModalOpen(false)
    setVehicleStops(EMPTY_STOPS)
  }, [selectedDate, vehicleId])

  // 1st click on the bus opens the identity tooltip; the 2nd (tooltip already
  // open) opens the Vehicle Route panel — the main map's two-step flow.
  const handleVehicleClick = () => {
    if (!infoOpen) {
      setInfoOpen(true)
      return
    }
    if (!vehicleId) return
    setRouteModalOpen(true)
    fetchVehicleStops()
  }

  const closeRoutePanel = () => {
    setRouteModalOpen(false)
    setInfoOpen(false)
  }

  // Shape the panel's vehicle the way the main map passes it.
  const routeVehicle = useMemo(
    () => ({
      vehicleId,
      vehicleName: vehicleDetails?.vehicle_name,
      vehicleModel: vehicleDetails?.vehicle_model,
      registeringNumber: vehicleDetails?.registering_number,
      ud_sn: deviceInfo?.ud_sn,
      lat: deviceInfo?.lat,
      lng: deviceInfo?.lng,
      remaining: deviceInfo?.remaining,
      is_online: deviceInfo?.is_online,
      report_time: deviceInfo?.report_time,
    }),
    [vehicleId, vehicleDetails, deviceInfo]
  )

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance)
    setMapLoaded(true)
  }, [])

  const shiftedPolyline = useMemo(() => {
    return offsetPolylinePath(polylinePath, 2)
  }, [polylinePath])

  const onZoomChanged = useCallback(() => {
    if (map) {
      setZoom(map.getZoom())
    }
  }, [map])

  const getMarkerIcon = useCallback(
    (logoUrl) => {
      if (!mapLoaded || !window.google || !window.google.maps) return null
      const scaleFactor = Math.pow(0.95, 14 - zoom)
      const size = Math.round(60 * scaleFactor)
      return {
        url: logoUrl || mapBus,
        scaledSize: new window.google.maps.Size(size, size),
      }
    },
    [mapLoaded, zoom]
  )

  const loadPolyline = useCallback(async () => {
    if (deviceInfo?.polyline?.length > 1) {
      const path = deviceInfo?.polyline
      if (path?.length) {
        setPolylinePath(path)
        return
      }
    }
    setPolylinePath(null)
  }, [deviceInfo])

  useEffect(() => {
    if (deviceInfo?.lat && deviceInfo?.lng) {
      const lat = parseFloat(deviceInfo.lat)
      const lng = parseFloat(deviceInfo.lng)

      if (!isNaN(lat) && !isNaN(lng)) {
        const newCenter = { lat, lng }
        setCenter(newCenter)
        if (map && isFirstLoad.current) {
          map.panTo(newCenter)
          isFirstLoad.current = false
        }
      }
    } else if (directionShapes.length) {
      // No GPS fix (vehicle without a router, or nothing reported yet):
      // still open the map on its line.
      const path = directionShapes[0].path
      setCenter(path[Math.floor(path.length / 2)])
    } else {
      setCenter(null)
    }

    loadPolyline()
  }, [deviceInfo, map, loadPolyline, directionShapes])

  // Frame the whole line (plus the bus) the first time it is drawn.
  useEffect(() => {
    if (!map || !window.google?.maps || !directionShapes.length) return
    const key = `${vehicleId}|${directionShapes.map((d) => d.key).join(',')}`
    if (fittedKey.current === key) return
    const bounds = new window.google.maps.LatLngBounds()
    directionShapes.forEach((d) => d.path.forEach((p) => bounds.extend(p)))
    if (hasVehiclePosition) {
      bounds.extend({ lat: parseFloat(deviceInfo.lat), lng: parseFloat(deviceInfo.lng) })
    }
    map.fitBounds(bounds, 40)
    // A very short line (or stops parked on one coordinate) would fit at
    // street-number zoom; keep it readable.
    window.google.maps.event.addListenerOnce(map, 'idle', () => {
      if (map.getZoom() > 17) map.setZoom(17)
    })
    fittedKey.current = key
  }, [map, directionShapes, vehicleId, hasVehiclePosition, deviceInfo])

  return (
    <>
      {isMapScriptLoaded && center ? (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>

          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={14}
            onLoad={onMapLoad}
            onZoomChanged={onZoomChanged}
            options={{ gestureHandling: 'greedy', disableDefaultUI: true }}
          >
            {/* Marker */}
            {hasVehiclePosition && (
              <Marker
                position={center}
                onClick={handleVehicleClick}
                icon={getMarkerIcon(vehicleDetails?.logo)}
              />
            )}

            {/* Status Dot */}
            {hasVehiclePosition && moment(selectedDate).isSame(new Date(), 'day') && (
              <OverlayView
                position={center}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                {(() => {
                  const baseSize = 35
                  const scale = Math.pow(0.95, 14 - zoom)
                  const size = Math.round(baseSize * scale)
                  return (
                    <div
                      style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: deviceInfo?.is_online ? '#00ff00' : '#ff0000',
                        borderRadius: '50%',
                        border: '2px solid white',
                        position: 'relative',
                        bottom: `${size + 8}px`,
                        left: `${(size / 2) - 6}px`,
                        zIndex: 1000,
                      }}
                    />
                  )
                })()}
              </OverlayView>
            )}

            {/* Info Window */}
            {hasVehiclePosition && infoOpen && (
              <InfoWindow
                position={center}
                onCloseClick={() => setInfoOpen(false)}
                options={{
                  pixelOffset: new window.google.maps.Size(0, -50),
                }}
              >
                <div style={{ fontSize: '14px', padding: '5px' }}>
                  <div>
                    <strong>{translations[language]?.vechicleName}:</strong>{' '}
                    {vehicleDetails?.vehicle_name}
                  </div>
                  <div>
                    <strong>{translations[language]?.vehicleModelLabel}:</strong>{' '}
                    {vehicleDetails?.vehicle_model}
                  </div>
                  {Number.isFinite(parseFloat(deviceInfo?.lat)) && (
                    <div>
                      <strong>📍</strong> {parseFloat(deviceInfo.lat).toFixed(6)},{' '}
                      {parseFloat(deviceInfo.lng).toFixed(6)}
                    </div>
                  )}
                  {deviceInfo?.report_time && (
                    <div>
                      <strong>🕒</strong> {formatReportTime(deviceInfo.report_time)}
                    </div>
                  )}
                </div>
              </InfoWindow>
            )}

            {/* The assigned line, one colour per direction. Drawn under the
                matched trip (blue) - dimmed while a trip is matched so the
                live geometry stays the loudest line on the map. */}
            {directionShapes.map((d) => (
              <Polyline
                key={`dir-${d.key}`}
                path={d.path}
                options={{
                  strokeColor: d.color,
                  strokeWeight: scheduleRoute?.shape?.length > 1 ? 3 : 4,
                  strokeOpacity: scheduleRoute?.shape?.length > 1 ? 0.45 : 0.9,
                  zIndex: 1,
                  icons: [
                    {
                      icon: {
                        path: window.google?.maps?.SymbolPath?.FORWARD_OPEN_ARROW,
                        scale: 2.5,
                        strokeColor: d.color,
                      },
                      offset: '0%',
                      repeat: '350px',
                    },
                  ],
                }}
              />
            ))}

            {/* Trip route (blue): the schedule's official road-following
                geometry. Vehicle route (red): the bus's actual GPS trace.
                Both drawn together so the bus's drift from its scheduled
                line stays visible. */}
            {scheduleRoute?.shape?.length > 1 && (
              <Polyline
                path={scheduleRoute.shape}
                options={{
                  strokeColor: '#4285F4',
                  strokeWeight: 4,
                  strokeOpacity: 1,
                  icons: [
                    {
                      icon: {
                        path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW,
                        scale: 3,
                        strokeColor: '#4285F4',
                      },
                      offset: '0%',
                      repeat: '450px',
                    },
                  ],
                }}
              />
            )}
            {/* Drawn as soon as the trace arrives (no longer waits for the
                schedule call). While the server is still road-snapping it
                (snapped === false) the raw GPS line is shown lighter and
                without direction arrows, so noise does not read as travel. */}
            {shiftedPolyline?.length > 1 && (
              <Polyline
                path={shiftedPolyline}
                options={
                  deviceInfo?.snapped === false
                    ? {
                        strokeColor: '#FF0000',
                        strokeWeight: 3,
                        strokeOpacity: 0.55,
                        icons: [],
                      }
                    : {
                        strokeColor: '#FF0000',
                        strokeWeight: 4,
                        strokeOpacity: 1,
                        icons: [
                          {
                            icon: {
                              path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW,
                              scale: 3,
                              strokeColor: '#FF0000',
                            },
                            offset: '0%',
                            repeat: '200px',
                          },
                        ],
                      }
                }
              />
            )}

            {/* The matched trip's stops — scheduled/estimated times on click,
                names at street-level zoom. Same palette as the main map. */}
            {mapLoaded &&
              window.google?.maps &&
              (scheduleRoute?.stops || [])
                .filter((s) => s?.lat != null && s?.lng != null)
                .map((s) => (
                  <Marker
                    key={`stop-${s.gtfs_stop_id}-${s.sequence}`}
                    position={{ lat: parseFloat(s.lat), lng: parseFloat(s.lng) }}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: s.status === 'current' ? 7 : s.status === 'past' ? 4 : 5,
                      fillColor:
                        s.status === 'current'
                          ? '#ff6d00'
                          : s.status === 'past'
                            ? '#90a4ae'
                            : '#1a73e8',
                      fillOpacity: s.status === 'past' ? 0.6 : 1,
                      strokeColor: '#ffffff',
                      strokeWeight: 2,
                      labelOrigin: new window.google.maps.Point(0, -2.2),
                    }}
                    label={
                      zoom >= STOP_LABEL_MIN_ZOOM
                        ? {
                            text: s.name?.length > 22 ? `${s.name.slice(0, 20)}…` : s.name,
                            color: '#0d47a1',
                            fontSize: '11px',
                            fontWeight: '700',
                          }
                        : undefined
                    }
                    zIndex={999}
                    onClick={() =>
                      setSelectedStopInfo({
                        ...s,
                        lat: parseFloat(s.lat),
                        lng: parseFloat(s.lng),
                      })
                    }
                  />
                ))}

            {/* The line's own stops - only while no trip is matched (the
                matched trip's stops above carry the live ETAs instead). */}
            {mapLoaded &&
              window.google?.maps &&
              !(scheduleRoute?.stops?.length > 0) &&
              directionShapes.map((d) =>
                d.stops.map((s) => (
                  <Marker
                    key={`line-stop-${d.key}-${s.gtfs_stop_id}-${s.stop_sequence}`}
                    position={{ lat: s.lat, lng: s.lng }}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 4,
                      fillColor: d.color,
                      fillOpacity: 1,
                      strokeColor: '#ffffff',
                      strokeWeight: 2,
                      labelOrigin: new window.google.maps.Point(0, -2.2),
                    }}
                    label={
                      zoom >= STOP_LABEL_MIN_ZOOM && s.stop_name
                        ? {
                            text:
                              s.stop_name.length > 22 ? `${s.stop_name.slice(0, 20)}...` : s.stop_name,
                            color: '#0d47a1',
                            fontSize: '11px',
                            fontWeight: '700',
                          }
                        : undefined
                    }
                    zIndex={998}
                    onClick={() =>
                      setSelectedStopInfo({ name: s.stop_name || s.gtfs_stop_id, lat: s.lat, lng: s.lng })
                    }
                  />
                ))
              )}

            {selectedStopInfo && (
              <InfoWindow
                position={{ lat: selectedStopInfo.lat, lng: selectedStopInfo.lng }}
                onCloseClick={() => setSelectedStopInfo(null)}
              >
                <div>
                  <div>
                    <strong>{selectedStopInfo.name}</strong>
                  </div>
                  {selectedStopInfo.scheduled && (
                    <div>
                      🕒 {translations[language]?.scheduledShort}:{' '}
                      <strong>{selectedStopInfo.scheduled}</strong>
                      {selectedStopInfo.estimated &&
                        selectedStopInfo.estimated !== selectedStopInfo.scheduled && (
                          <>
                            {' · '}
                            {translations[language]?.estimatedShort}:{' '}
                            <strong>{selectedStopInfo.estimated}</strong>
                          </>
                        )}
                    </div>
                  )}
                </div>
              </InfoWindow>
            )}

            {/* Pulsing ring marking the live bus, matching the main map. */}
            {hasVehiclePosition && moment(selectedDate).isSame(new Date(), 'day') && (
              <OverlayView position={center} mapPaneName={OverlayView.OVERLAY_LAYER}>
                <div className="vehicle-highlight-ring" />
              </OverlayView>
            )}

            {/* Start (green S) / End (red E) of today's GPS trace — same
                markers as every other map. */}
            <TraceEndpoints path={shiftedPolyline} />

            {/* Capolinea of each drawn direction: first and last stop when the
                direction has stops, else the two ends of its geometry. */}
            {showLineEndpoints &&
              directionShapes.map((d) => {
                const ends =
                  d.stops.length > 1
                    ? [d.stops[0], d.stops[d.stops.length - 1]]
                    : [d.path[0], d.path[d.path.length - 1]]
                return (
                  <TraceEndpoints
                    key={`line-ends-${d.key}`}
                    path={ends.map((p) => ({ lat: p.lat, lng: p.lng }))}
                  />
                )
              })}
          </GoogleMap>

          {directionShapes.length > 0 && (
            <div className='vehicle-map-legend'>
              {directionShapes.map((d) => (
                <div key={`legend-${d.key}`} className='vehicle-map-legend-row'>
                  <span className='vehicle-map-legend-swatch' style={{ background: d.color }} />
                  <span>
                    {d.headsign || `${translations[language]?.direction} ${d.key}`}
                  </span>
                </div>
              ))}
            </div>
          )}

          {isLoading && (
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: '1.5em',
              color: '#333',
              backgroundColor: 'rgb(0 0 0 / 30%)',
            }}>
              <div className="spinner-border" role="status">
                <div class="dot-spinner">
                  <div class="dot-spinner__dot"></div>
                  <div class="dot-spinner__dot"></div>
                  <div class="dot-spinner__dot"></div>
                  <div class="dot-spinner__dot"></div>
                  <div class="dot-spinner__dot"></div>
                  <div class="dot-spinner__dot"></div>
                  <div class="dot-spinner__dot"></div>
                  <div class="dot-spinner__dot"></div>
                </div>
                <span className="sr-only">Loading Map...</span>
              </div>
            </div>
          )}

          <CustomModal
            isVisible={routeModalOpen}
            onClose={closeRoutePanel}
            title={translations[language]?.vehicleRouteTitle}
            language={language}
          >
            {routeModalOpen && (
              <VehicleRoute
                language={language}
                vehicle={routeVehicle}
                forecastDevice={routeVehicle?.ud_sn}
                vehicleStops={vehicleStops}
                past={vehicleStops?.past}
                current={vehicleStops?.current}
                future={vehicleStops?.future}
                loading={isStopsLoading || !scheduleLoaded}
                schedule={scheduleRoute}
              />
            )}
          </CustomModal>
        </div>
      ) : (
        <div className='vm-loading'>
          <div className='vm-loading-shimmer' />
          <span className='vm-loading-text'>{translations[language]?.loadingDeviceLocation}</span>
        </div>
      )}
    </>
  )
}

export default VehicleMap